import React, { useCallback, useMemo } from 'react';

import { USER_API_URL } from '@editor/api/constants';
import {
  NotificationType,
  ProjectCommentNotification as ProjectCommentNotificationData,
} from '@editor/types';

import NotificationHeader from '../NotificationHeader/NotificationHeader';
import PreviewImage from '../PreviewImage/PreviewImage';

import {
  NotificationWrapper,
  Bubble,
  BubbleTextWrapper,
  BubbleText,
} from './styles';
import { NotificationProps } from './types';

const ProjectCommentNotification: React.FC<
  NotificationProps<ProjectCommentNotificationData>
> = ({ notification, onReadNotification }) => {
  const isGrouped = notification.data.totalComments > 1;

  const message = useMemo(() => {
    switch (notification.type) {
      case NotificationType.PRESENTATION_THREAD:
      case NotificationType.PROJECT_THREAD:
        return isGrouped
          ? `left ${notification.data.totalComments} comments on ${notification.data.designName}`
          : `commented on ${notification.data.designName}`;

      case NotificationType.PRESENTATION_THREAD_COMMENT:
      case NotificationType.PROJECT_THREAD_COMMENT:
        return isGrouped
          ? `left ${notification.data.totalComments} replies on ${notification.data.designName}`
          : `replied on ${notification.data.designName}`;
      default:
        return '';
    }
  }, [isGrouped, notification]);

  const user = useMemo(() => {
    if (!notification.relatedUser) {
      return undefined;
    }

    const commenterLength = notification.data.commenterUserIds.length;
    if (commenterLength === 1) {
      return notification.relatedUser;
    } else {
      return {
        ...notification.relatedUser,
        name: `${notification.relatedUser.name} and ${
          commenterLength - 1
        } others`,
      };
    }
  }, [notification]);

  const handleClick = useCallback((): void => {
    onReadNotification(notification.id, {
      redirectPath: `/${notification.data.path}`,
    });
  }, [onReadNotification, notification.id, notification.data.path]);

  const previewImageUrl = `${USER_API_URL}/designs/${notification.data.designId}/preview?width=70&height=70`;

  return (
    <NotificationWrapper role="button" onClick={handleClick}>
      <NotificationHeader
        createdAt={notification.createdAt}
        isUnseen={!notification.seenAt}
        message={message}
        user={user}
      />
      {!isGrouped && (
        <Bubble>
          <PreviewImage
            key={notification.data.designId}
            noMargin
            src={previewImageUrl}
          />
          <BubbleTextWrapper>
            <BubbleText>{`"${notification.data.comment}"`}</BubbleText>
          </BubbleTextWrapper>
        </Bubble>
      )}
    </NotificationWrapper>
  );
};

export default ProjectCommentNotification;
